import { EditMemberOnboardingSearchParams } from "@/admin/member-onboarding/EditMemberOnboardingContext"
import { RemoveAppModalQuery } from "@/apps/remove-app-button/__generated__/RemoveAppModalQuery.graphql"
import { useAppLabel } from "@/apps/util/appUtil"
import { useLabel } from "@/core/context/LabelsContext"
import ROUTE_NAMES from "@/core/route/util/routeNames"
import { GlobalID } from "@/relay/RelayTypes"
import Relay from "@/relay/relayUtils"
import { DiscoButton } from "@disco-ui"
import DiscoConfirmationModal, {
  DiscoConfirmationModalProps,
} from "@disco-ui/modal/DiscoConfirmationModal"
import DiscoWarningModal from "@disco-ui/modal/DiscoWarningModal"
import { TestIDProps } from "@utils/typeUtils"
import { setSearchParams } from "@utils/url/urlUtils"
import { useLazyLoadQuery } from "react-relay"
import { graphql } from "relay-runtime"

type Props = TestIDProps &
  Pick<
    DiscoConfirmationModalProps,
    "isOpen" | "onClose" | "title" | "description" | "confirmButtonProps"
  > & {
    appId: GlobalID
  }

function RemoveAppModal(props: Props) {
  const { testid = "RemoveAppModal", appId, ...modalProps } = props
  const memberLabel = useLabel("admin_member")

  const { node } = useLazyLoadQuery<RemoveAppModalQuery>(
    graphql`
      query RemoveAppModalQuery($id: ID!) {
        node(id: $id) {
          ... on ProductApp {
            __typename
            customAppTitle
            isOnboardingRedirect
            # fields for useAppLabel
            kind
            feed {
              name
            }
          }
        }
      }
    `,
    { id: appId },
    { fetchPolicy: "network-only" }
  )

  const app = Relay.narrowNodeType(node, "ProductApp")
  const appLabel = useAppLabel(app)
  if (!app) return null

  // Don't allow deleting if onboarding completion redirects to this app
  if (app.isOnboardingRedirect) {
    return (
      <DiscoWarningModal
        testid={"OnboardingRedirectWarning"}
        variant={"warning"}
        isOpen={modalProps.isOpen}
        onClose={modalProps.onClose}
        modalContentLabel={"Onboarding Warning Dialog"}
        title={`This app is where ${memberLabel.plural} land after onboarding.`}
        description={`To remove this app, edit onboarding settings and choose a different app for ${memberLabel.plural} to land on after they complete onboarding.`}
        customConfirmationButton={
          <DiscoButton
            width={"100%"}
            to={{
              pathname: ROUTE_NAMES.ADMIN.MEMBERS.ONBOARDING.ROOT,
              search: setSearchParams<EditMemberOnboardingSearchParams>("", {
                openKind: "onboarding_complete",
              }),
            }}
          >
            {"Edit Onboarding"}
          </DiscoButton>
        }
      />
    )
  }

  return (
    <DiscoConfirmationModal
      {...modalProps}
      testid={testid}
      modalContentLabel={"Remove App Dialog"}
      typeToConfirmText={app.customAppTitle || appLabel}
    />
  )
}

export default Relay.withSkeleton({
  component: RemoveAppModal,
  skeleton: () => null,
})
